import { useState, useEffect } from 'react';
import { usePermission } from '../hooks/usePermission';
import axios from 'axios';
import {
  Users, Shield, ShieldCheck, Link, Layers, Zap, Mail, Globe, Database,
  Settings2, SlidersHorizontal, UserPlus, X
} from 'lucide-react';
import { toast } from 'sonner';
import { usersService, rolesService, User, UserCreate, UserUpdate, Role } from '../services/users';
import { userInvitationsService, UserInvitation } from '../services/userInvitations';
import { useTranslation } from '../i18n/I18nContext';
import { getErrorMessage } from '../utils/apiTransformers';
import { Integrations } from '../components/admin/Integrations';
import { EditUserModal } from '../components/admin/EditUserModal';
import { GovernanceSLAPanel } from '../components/GovernanceSLAPanel';
import { ScheduledReportsManager } from '../components/ScheduledReportsManager';
import { APITokensSection } from '../components/admin/APITokensSection';
import { WebhooksSection } from '../components/admin/WebhooksSection';
import { GroupsSection } from '../components/admin/GroupsSection';
import { EnterpriseTrustSection } from '../components/admin/EnterpriseTrustSection';
import { SecurityOverviewSection } from '../components/admin/SecurityOverviewSection';
import { RolesOverview } from '../components/admin/RolesOverview';
import { RegionalFormatSection } from '../components/admin/RegionalFormatSection';
import { WorkspaceSetupSection } from '../components/admin/WorkspaceSetupSection';
import { WorkspaceDataSection } from '../components/admin/WorkspaceDataSection';
import type { TrustTab } from '../components/admin/EnterpriseTrustSection';
import { CustomFieldsManager } from '../components/project/CustomFieldsManager';
import { useCurrentOrgId } from '../contexts/AuthContext';
import type { ApiErrorLike } from '../utils/apiError'


export default function AdminSettings() {
  // Only admins manage people and roles; everyone else gets a read-only list.
  const canManage = usePermission('admin:manage');
  const { t } = useTranslation();
  const [activeSection, setActiveSection] = useState('users');
  const [trustTab, setTrustTab] = useState<TrustTab>('policy');
  const currentOrgId = useCurrentOrgId();
  const openTrust = (tab: TrustTab) => {
    setTrustTab(tab);
    setActiveSection('trust');
  };
  const [usersTab, setUsersTab] = useState<'users' | 'roles'>('users');
  const [editingUser, setEditingUser] = useState<number | null>(null);
  const [selectedRole, setSelectedRole] = useState<string>('');
  const [userStatus, setUserStatus] = useState<string>('Active');

  // Backend data
  const [backendUsers, setBackendUsers] = useState<User[]>([]);
  const [backendRoles, setBackendRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formData, setFormData] = useState<UserCreate>({
    email: '',
    password: '',
    full_name: '',
    is_active: true,
    role_ids: [],
  });

  // Sprint 32 — team invitations
  const [pendingInvites, setPendingInvites] = useState<UserInvitation[]>([]);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteSending, setInviteSending] = useState(false);
  const [inviteForm, setInviteForm] = useState<{ email: string; full_name: string; role_id: number | ''; access: 'member' | 'viewer' }>({
    email: '',
    full_name: '',
    role_id: '',
    access: 'member',
  });

  const sections = [
    { id: 'users', label: 'Users & Roles', icon: Users },
    { id: 'groups', label: 'Groups', icon: Users },
    { id: 'security', label: 'Security & Audit', icon: Shield },
    { id: 'trust', label: 'Enterprise Trust', icon: ShieldCheck },
    { id: 'setup', label: 'Workspace Setup', icon: Settings2 },
    { id: 'governance', label: 'Governance', icon: Layers },
    { id: 'custom-fields', label: 'Custom Fields', icon: SlidersHorizontal },
    { id: 'integrations', label: 'Integrations', icon: Link },
    { id: 'api', label: 'API & Webhooks', icon: Zap },
    { id: 'regional', label: 'Regional Format', icon: Globe },
    { id: 'data', label: 'Your Data', icon: Database },
  ];

  // Real role names for EditUserModal (was a static list of job titles
  // that matched no backend role, so changing a role silently did nothing).
  const roleNames = backendRoles.map((r) => r.name);

  useEffect(() => {
    if (activeSection === 'users') {
      fetchUsers();
      fetchRoles();
      fetchInvitations();
    }
  }, [activeSection]);

  const fetchInvitations = async () => {
    try {
      const data = await userInvitationsService.list('pending');
      setPendingInvites(data);
    } catch (error) {
      console.error('Error fetching invitations:', error);
      // Non-blocking — the users table is the primary view. Leave the
      // pending list empty rather than masking it with stale data.
      setPendingInvites([]);
    }
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const data = await usersService.list();
      setBackendUsers(data);
    } catch (errorRaw) {
      const error = errorRaw as ApiErrorLike
      console.error('Error fetching users:', error);
      toast.error(getErrorMessage(error) || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const data = await rolesService.list();
      setBackendRoles(data);
    } catch (errorRaw) {
      const error = errorRaw as ApiErrorLike
      if (error?.response?.status === 403) return;  // not an admin: nothing to show
      console.error('Error fetching roles:', error);
      toast.error(getErrorMessage(error) || 'Failed to load roles');
    }
  };

  const handleCreate = async () => {
    if (!formData.email || !formData.password) {
      toast.error('Email and password are required');
      return;
    }

    setLoading(true);
    try {
      await usersService.create(formData);
      toast.success('User created successfully');
      setShowCreateModal(false);
      resetForm();
      fetchUsers();
    } catch (errorRaw) {
      const error = errorRaw as ApiErrorLike
      console.error('Error creating user:', error);
      toast.error(getErrorMessage(error) || 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Are you sure you want to delete this user?')) {
      return;
    }

    try {
      await usersService.delete(id);
      toast.success('User deleted successfully');
      fetchUsers();
    } catch (errorRaw) {
      const error = errorRaw as ApiErrorLike
      console.error('Error deleting user:', error);
      toast.error(getErrorMessage(error) || 'Failed to delete user');
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      full_name: '',
      is_active: true,
      role_ids: [],
    });
  };

  // ── Team invitations (Sprint 32) ──────────────────────────────────────────
  const resetInviteForm = () => setInviteForm({ email: '', full_name: '', role_id: '', access: 'member' });

  const handleSendInvite = async () => {
    const email = inviteForm.email.trim();
    if (!email) {
      toast.error(t('invite.emailRequired'));
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error(t('invite.emailInvalid'));
      return;
    }

    setInviteSending(true);
    try {
      const created = await userInvitationsService.create({
        email,
        full_name: inviteForm.full_name.trim() || undefined,
        role_ids: inviteForm.access === 'viewer' || inviteForm.role_id === '' ? undefined : [inviteForm.role_id],
        access: inviteForm.access,
      });
      if (created?.email_sent === false) {
        toast.warning(created.email_warning || t('invite.error'));
      } else {
        toast.success(t('invite.sent', { email }));
      }
      setShowInviteModal(false);
      resetInviteForm();
      fetchInvitations();
    } catch (error) {
      // Sprint 30 gate — an unverified owner can't drive the invite mailer.
      const status = axios.isAxiosError(error) ? error.response?.status : undefined;
      const code = axios.isAxiosError(error)
        ? (error.response?.data as { detail?: { code?: string } } | undefined)?.detail?.code
        : undefined;
      if (status === 403 && code === 'email_unverified') {
        toast.error(t('invite.unverified'));
      } else {
        toast.error(getErrorMessage(error) || t('invite.error'));
      }
    } finally {
      setInviteSending(false);
    }
  };

  const handleResendInvite = async (id: number) => {
    try {
      await userInvitationsService.resend(id);
      toast.success(t('invite.resent'));
    } catch (error) {
      console.error('Error resending invitation:', error);
      toast.error(getErrorMessage(error) || t('invite.resendError'));
    }
  };

  const handleRevokeInvite = async (id: number) => {
    if (!confirm(t('invite.revokeConfirm'))) return;
    try {
      await userInvitationsService.cancel(id);
      toast.success(t('invite.revoked'));
      fetchInvitations();
    } catch (error) {
      console.error('Error revoking invitation:', error);
      toast.error(getErrorMessage(error) || t('invite.revokeError'));
    }
  };

  const toggleRole = (roleId: number) => {
    setFormData((prev) => {
      const roleIds = prev.role_ids || [];
      if (roleIds.includes(roleId)) {
        return { ...prev, role_ids: roleIds.filter((id) => id !== roleId) };
      } else {
        return { ...prev, role_ids: [...roleIds, roleId] };
      }
    });
  };

  // Convert backend User to EditUserModal format
  const getUserForModal = (user: User | null) => {
    if (!user) return null;
    return {
      id: user.id,
      name: user.full_name || user.email,
      email: user.email,
      role: user.roles.length > 0 ? user.roles[0].name : '',
      status: user.is_active ? 'Active' : 'Inactive',
    };
  };

  const handleEditUser = (user: User) => {
    setEditingUser(user.id);
    setSelectedRole(user.roles.length > 0 ? user.roles[0].name : '');
    setUserStatus(user.is_active ? 'Active' : 'Inactive');
    setFormData({
      email: user.email,
      password: '',
      full_name: user.full_name || '',
      is_active: user.is_active,
      role_ids: user.roles.map((r) => r.id),
    });
  };

  const handleSaveUser = async () => {
    if (!editingUser) return;

    // Map selected role name to role ID
    const selectedRoleObj = backendRoles.find(r => r.name === selectedRole);
    const updateData: UserUpdate = {
      email: formData.email,
      full_name: formData.full_name,
      is_active: userStatus === 'Active',
      role_ids: selectedRoleObj ? [selectedRoleObj.id] : formData.role_ids,
    };
    if (formData.password) {
      updateData.password = formData.password;
    }

    setLoading(true);
    try {
      await usersService.update(editingUser, updateData);
      toast.success('User updated successfully');
      setEditingUser(null);
      resetForm();
      fetchUsers();
    } catch (errorRaw) {
      const error = errorRaw as ApiErrorLike
      console.error('Error updating user:', error);
      toast.error(getErrorMessage(error) || 'Failed to update user');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex h-[calc(100vh-64px)] bg-[#f0f4f8]">
      {/* Left Sidebar */}
      <div className="w-64 bg-white border-r border-gray-200 overflow-y-auto">
        <div className="p-4 border-b border-gray-200">
          <h2 className="font-medium text-[#102a43]">Settings</h2>
        </div>
        <div className="p-2">
          {sections.map((section) => {
            const Icon = section.icon;
            return (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg mb-1 transition-colors ${
                  activeSection === section.id
                    ? 'bg-blue-50 text-blue-600'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span className="text-sm">{section.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-6 space-y-6 max-w-[1400px]">
          {activeSection === 'users' && (
            <>
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-2xl mb-1 text-[#102a43]">Users & Roles</h1>
                  <p className="text-gray-600">Manage user access and permissions</p>
                </div>
                {canManage && <div className="flex items-center gap-2">
                  {usersTab === 'users' && (
                    <button
                      onClick={() => { resetInviteForm(); setShowInviteModal(true); }}
                      className="px-4 py-2 border-2 border-[#486581] text-[#486581] hover:bg-[#486581]/10 rounded-lg transition-colors flex items-center gap-2"
                    >
                      <Mail className="w-4 h-4" />
                      {t('invite.button')}
                    </button>
                  )}
                  <button
                    onClick={() => setShowCreateModal(true)}
                    className="px-4 py-2 bg-[#486581] hover:bg-[#5a7692] text-white rounded-lg transition-colors flex items-center gap-2"
                  >
                    <UserPlus className="w-4 h-4" />
                    {usersTab === 'users' ? 'Add User' : 'Create Role'}
                  </button>
                </div>}
              </div>

              {/* Tabs */}
              <div className="bg-white rounded-lg border-2 border-[#bcccdc] overflow-hidden shadow-md">
                <div className="flex border-b-2 border-[#bcccdc]">
                  <button
                    onClick={() => setUsersTab('users')}
                    className={`flex-1 px-6 py-3 font-medium transition-colors ${
                      usersTab === 'users'
                        ? 'bg-[#486581] text-white'
                        : 'bg-[#f0f4f8] text-[#486581] hover:bg-[#bcccdc]/30'
                    }`}
                  >
                    Users
                  </button>
                  <button
                    onClick={() => setUsersTab('roles')}
                    className={`flex-1 px-6 py-3 font-medium transition-colors ${
                      usersTab === 'roles'
                        ? 'bg-[#486581] text-white'
                        : 'bg-[#f0f4f8] text-[#486581] hover:bg-[#bcccdc]/30'
                    }`}
                  >
                    Role-Based Permissions
                  </button>
                </div>

                {usersTab === 'users' ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-[#f0f4f8] border-b-2 border-[#bcccdc]">
                        <tr>
                          <th className="text-left px-6 py-3 text-sm text-[#486581] font-medium">Name</th>
                          <th className="text-left px-6 py-3 text-sm text-[#486581] font-medium">Email</th>
                          <th className="text-left px-6 py-3 text-sm text-[#486581] font-medium">Role</th>
                          <th className="text-left px-6 py-3 text-sm text-[#486581] font-medium">Status</th>
                          <th className="text-left px-6 py-3 text-sm text-[#486581] font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loading ? (
                          <tr>
                            <td colSpan={5} className="px-6 py-4 text-center text-[#829ab1]">Loading users...</td>
                          </tr>
                        ) : backendUsers.length === 0 ? (
                          <tr>
                            <td colSpan={5} className="px-6 py-4 text-center text-[#829ab1]">No users found.</td>
                          </tr>
                        ) : (
                          backendUsers.map((user, index) => (
                            <tr key={user.id} className={`border-b border-[#f0f4f8] hover:bg-[#f0f4f8] transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-[#f0f4f8]/30'}`}>
                              <td className="px-6 py-4 font-medium text-[#102a43]">{user.full_name || 'No name'}</td>
                              <td className="px-6 py-4 text-sm text-[#486581]">{user.email}</td>
                              <td className="px-6 py-4">
                                {user.roles.length > 0 ? (
                                  <span className="px-3 py-1 bg-[#486581]/10 text-[#486581] rounded-lg text-xs font-medium border border-[#486581]/20">
                                    {user.roles.map(r => r.name).join(', ')}
                                  </span>
                                ) : (
                                  <span className="text-sm text-[#829ab1]">No roles</span>
                                )}
                              </td>
                              <td className="px-6 py-4">
                                <span className={`px-3 py-1 rounded-lg text-xs font-medium border ${
                                  user.is_active 
                                    ? 'bg-green-100 text-green-700 border-green-300' 
                                    : 'bg-red-100 text-red-700 border-red-300'
                                }`}>
                                  {user.is_active ? 'Active' : 'Inactive'}
                                </span>
                              </td>
                              <td className="px-6 py-4">
                                {canManage && <div className="flex gap-2">
                                  <button 
                                    onClick={() => handleEditUser(user)}
                                    className="text-[#486581] hover:text-[#102a43] text-sm font-medium"
                                  >
                                    Edit
                                  </button>
                                  <button 
                                    onClick={() => handleDelete(user.id)}
                                    className="text-red-600 hover:text-red-700 text-sm font-medium"
                                  >
                                    Delete
                                  </button>
                                </div>}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="p-6">
                    <RolesOverview roles={backendRoles} />
                  </div>
                )}
              </div>

              {/* Pending invitations (Sprint 32) */}
              {usersTab === 'users' && (
                <div className="bg-white rounded-lg border-2 border-[#bcccdc] overflow-hidden shadow-md">
                  <div className="px-6 py-3 bg-[#f0f4f8] border-b-2 border-[#bcccdc] flex items-center gap-2">
                    <Mail className="w-4 h-4 text-[#486581]" />
                    <h2 className="text-sm font-medium text-[#486581]">{t('invite.pendingTitle')}</h2>
                  </div>
                  {pendingInvites.length === 0 ? (
                    <p className="px-6 py-4 text-sm text-[#829ab1]">{t('invite.pendingEmpty')}</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-[#f0f4f8] border-b border-[#bcccdc]">
                          <tr>
                            <th className="text-left px-6 py-2 text-xs text-[#486581] font-medium">Email</th>
                            <th className="text-left px-6 py-2 text-xs text-[#486581] font-medium">Expires</th>
                            <th className="text-left px-6 py-2 text-xs text-[#486581] font-medium">{t('common.actions')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pendingInvites.map((inv, index) => (
                            <tr key={inv.id} className={`border-b border-[#f0f4f8] ${index % 2 === 0 ? 'bg-white' : 'bg-[#f0f4f8]/30'}`}>
                              <td className="px-6 py-3 text-sm text-[#102a43]">{inv.email}</td>
                              <td className="px-6 py-3 text-sm text-[#829ab1]">
                                {new Date(inv.expires_at).toLocaleDateString()}
                              </td>
                              <td className="px-6 py-3">
                                <div className="flex gap-3">
                                  <button
                                    onClick={() => handleResendInvite(inv.id)}
                                    className="text-[#486581] hover:text-[#102a43] text-sm font-medium"
                                  >
                                    {t('invite.resend')}
                                  </button>
                                  <button
                                    onClick={() => handleRevokeInvite(inv.id)}
                                    className="text-red-600 hover:text-red-700 text-sm font-medium"
                                  >
                                    {t('invite.revoke')}
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {activeSection === 'integrations' && (
            <Integrations />
          )}

          {activeSection === 'trust' && (
            <EnterpriseTrustSection key={trustTab} initialTab={trustTab} />
          )}

          {activeSection === 'security' && (
            <SecurityOverviewSection onEditPolicy={() => openTrust('policy')} />
          )}

          {activeSection === 'setup' && (
            <div className="space-y-8">
              <WorkspaceSetupSection />
              <div>
                <h2 className="mb-4 text-lg font-semibold text-foreground">Scheduled report delivery</h2>
                <div className="rounded-lg border border-border bg-card p-6">
                  <ScheduledReportsManager />
                </div>
              </div>
            </div>
          )}

          {activeSection === 'api' && (
            <>
              <div>
                <h1 className="text-2xl mb-1 text-[#102a43]">API &amp; Webhooks</h1>
                <p className="text-gray-600">Manage API tokens and webhook endpoints for external integrations</p>
              </div>
              <div className="bg-white rounded-lg border-2 border-[#bcccdc] shadow-md p-6">
                <APITokensSection />
              </div>
              <div className="bg-white rounded-lg border-2 border-[#bcccdc] shadow-md p-6">
                <WebhooksSection />
              </div>
            </>
          )}

          {activeSection === 'groups' && <GroupsSection />}

          {activeSection === 'governance' && (
            <div className="space-y-6">
              <div>
                <h1 className="mb-1 text-2xl text-foreground">Governance</h1>
                <p className="text-muted-foreground">Change-request deadlines, automatic escalation and the governance audit trail.</p>
              </div>
              <GovernanceSLAPanel />
            </div>
          )}

          {activeSection === 'custom-fields' && (
            <div className="space-y-6">
              <div>
                <h1 className="mb-1 text-2xl text-foreground">Custom fields</h1>
                <p className="text-muted-foreground">Extra fields your projects and tasks should carry, such as cost centre or client.</p>
              </div>
              <CustomFieldsManager organizationId={currentOrgId} />
            </div>
          )}

          {activeSection === 'regional' && <RegionalFormatSection />}

          {activeSection === 'data' && <WorkspaceDataSection onOpenDeletion={() => openTrust('tenant')} />}
        </div>
      </div>

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl border-2 border-[#bcccdc] shadow-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-[#102a43]">Create New User</h2>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
                className="text-[#829ab1] hover:text-[#102a43]"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#486581] mb-2">Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-2 border-2 border-[#bcccdc] rounded-lg focus:outline-none focus:border-[#486581] text-[#102a43]"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#486581] mb-2">Password *</label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full px-4 py-2 border-2 border-[#bcccdc] rounded-lg focus:outline-none focus:border-[#486581] text-[#102a43]"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#486581] mb-2">Full Name</label>
                <input
                  type="text"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  className="w-full px-4 py-2 border-2 border-[#bcccdc] rounded-lg focus:outline-none focus:border-[#486581] text-[#102a43]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#486581] mb-2">Roles</label>
                <div className="space-y-2 max-h-40 overflow-y-auto border-2 border-[#bcccdc] rounded-lg p-2">
                  {backendRoles.map((role) => (
                    <label key={role.id} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.role_ids?.includes(role.id) || false}
                        onChange={() => toggleRole(role.id)}
                        className="rounded border-[#bcccdc]"
                      />
                      <span className="text-sm text-[#102a43]">{role.name}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="rounded border-[#bcccdc]"
                />
                <label htmlFor="is_active" className="text-sm text-[#102a43] cursor-pointer">
                  Active
                </label>
              </div>
              <div className="flex gap-2 pt-4">
                <button
                  onClick={handleCreate}
                  disabled={loading || !formData.email || !formData.password}
                  className="flex-1 px-4 py-2 bg-[#486581] hover:bg-[#5a7692] text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                >
                  <UserPlus className="w-4 h-4" />
                  Create User
                </button>
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    resetForm();
                  }}
                  className="px-4 py-2 border-2 border-[#bcccdc] rounded-lg hover:bg-[#f0f4f8] transition-colors text-[#486581]"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Invite User Modal (Sprint 32) */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl border-2 border-[#bcccdc] shadow-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-[#102a43]">{t('invite.modalTitle')}</h2>
              <button
                onClick={() => { setShowInviteModal(false); resetInviteForm(); }}
                className="text-[#829ab1] hover:text-[#102a43]"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#486581] mb-2">{t('invite.emailLabel')} *</label>
                <input
                  type="email"
                  value={inviteForm.email}
                  onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                  className="w-full px-4 py-2 border-2 border-[#bcccdc] rounded-lg focus:outline-none focus:border-[#486581] text-[#102a43]"
                  required
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#486581] mb-2">{t('invite.nameLabel')}</label>
                <input
                  type="text"
                  value={inviteForm.full_name}
                  onChange={(e) => setInviteForm({ ...inviteForm, full_name: e.target.value })}
                  className="w-full px-4 py-2 border-2 border-[#bcccdc] rounded-lg focus:outline-none focus:border-[#486581] text-[#102a43]"
                />
              </div>
              <div>
                <label htmlFor="invite-access" className="block text-sm font-medium text-[#486581] mb-2">{t('invite.accessLabel')}</label>
                <select
                  id="invite-access"
                  value={inviteForm.access}
                  onChange={(e) => setInviteForm({ ...inviteForm, access: e.target.value === 'viewer' ? 'viewer' : 'member' })}
                  className="w-full px-4 py-2 border-2 border-[#bcccdc] rounded-lg focus:outline-none focus:border-[#486581] text-[#102a43] bg-white"
                >
                  <option value="member">{t('invite.accessMember')}</option>
                  <option value="viewer">{t('invite.accessViewer')}</option>
                </select>
              </div>
              {inviteForm.access === 'member' && (
              <div>
                <label className="block text-sm font-medium text-[#486581] mb-2">{t('invite.roleLabel')}</label>
                <select
                  value={inviteForm.role_id}
                  onChange={(e) => setInviteForm({ ...inviteForm, role_id: e.target.value === '' ? '' : Number(e.target.value) })}
                  className="w-full px-4 py-2 border-2 border-[#bcccdc] rounded-lg focus:outline-none focus:border-[#486581] text-[#102a43] bg-white"
                >
                  <option value="">{t('invite.roleNone')}</option>
                  {backendRoles.map((role) => (
                    <option key={role.id} value={role.id}>{role.name}</option>
                  ))}
                </select>
              </div>
              )}
              <div className="flex gap-2 pt-4">
                <button
                  onClick={handleSendInvite}
                  disabled={inviteSending || !inviteForm.email.trim()}
                  className="flex-1 px-4 py-2 bg-[#486581] hover:bg-[#5a7692] text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                >
                  <Mail className="w-4 h-4" />
                  {inviteSending ? t('invite.sending') : t('invite.send')}
                </button>
                <button
                  onClick={() => { setShowInviteModal(false); resetInviteForm(); }}
                  className="px-4 py-2 border-2 border-[#bcccdc] rounded-lg hover:bg-[#f0f4f8] transition-colors text-[#486581]"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      <EditUserModal
        user={getUserForModal(backendUsers.find(u => u.id === editingUser) || null)}
        isOpen={editingUser !== null}
        onClose={() => {
          setEditingUser(null);
          resetForm();
        }}
        selectedRole={selectedRole}
        setSelectedRole={setSelectedRole}
        userStatus={userStatus}
        setUserStatus={setUserStatus}
        roles={roleNames}
        onSave={handleSaveUser}
      />
    </div>
  );
}
